import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as SR from "reports/measures/SeverityResolver";

export const getMeasureWeek = (week: string | number): MD.MeasureWeek => {
    return week === MD.Month6Token ? MD.Month6Token : Number(week);
};

// export const includeMeasureWeek = (measureWeek: MD.MeasureWeek, minWeek: number, maxWeek: number): boolean => {
//     return (measureWeek === MD.Month6Token || (Number(measureWeek) >= minWeek && Number(measureWeek) <= maxWeek));
// };

export const isMonth6MeasureWeek = (measureWeek: MD.MeasureWeek): boolean => {
    return measureWeek === MD.Month6Token;
};

// export const isMonth6Measure = (measure: MD.Measure): boolean => {
//     return isMonth6MeasureWeek(measure.week);
// };

export const getMostRecentMeasureWeekRecord = <T, >(
    data: { week: MD.MeasureWeek; value: T }[]
): T | undefined => {
    const month6Record = data.find((item) => item.week === MD.Month6Token);
    if (month6Record) {
        return month6Record.value;
    }

    const numericRecords = data
        .filter((item) => typeof item.week === "number")
        .sort((a, b) => (a.week as number) - (b.week as number));

    if (numericRecords.length > 0) {
        return numericRecords[numericRecords.length - 1].value;
    }

    return undefined;
};

export const type = (measures: MD.Measure | MD.Measures): MD.MeasureType => {
    if (!measures || (Array.isArray(measures) && measures.length === 0)) {
        throw new Error("[measureType] Invalid measures provided: the 'measures' argument must be a Measure object or a non-empty array of Measure objects.");
    }
    return Array.isArray(measures) ? measures[0].type : measures.type;
};

export const name = (mt: MD.MeasureType): string => {
    return MP.MeasurePropertiesMap[mt].name;
};

export const isBehavioralType = (mt: MD.MeasureType): boolean => {
    return MP.MeasurePropertiesMap[mt].categories.includes(MD.MeasureCategory.BEHAVIORAL);
};

export const isPhysicalType = (mt: MD.MeasureType): boolean => {
    return MP.MeasurePropertiesMap[mt].categories.includes(MD.MeasureCategory.PHYSICAL);
};

export const isUpperRomType = (mt: MD.MeasureType): boolean => {
    return MP.MeasurePropertiesMap[mt].categories.includes(MD.MeasureCategory.UPPER_ROM);
};

export const isLowerRomType = (mt: MD.MeasureType): boolean => {
    return MP.MeasurePropertiesMap[mt].categories.includes(MD.MeasureCategory.LOWER_ROM);
};

export const isNeckRomType = (mt: MD.MeasureType): boolean => {
    return MP.MeasurePropertiesMap[mt].categories.includes(MD.MeasureCategory.NECK_ROM);
};

export const isRomType = (mt: MD.MeasureType): boolean => {
    return isUpperRomType(mt) || isLowerRomType(mt) || isNeckRomType(mt);
};

export const displayAsUnits = (measure: MD.Measure): string => {
    return `${measure.value.toString()}${isRomType(measure.type) ? "°" : ""}`;
};

export const lowerIsBetter = (mt: MD.MeasureType): boolean => {
    return MP.MeasurePropertiesMap[mt].lowerIsBetter;
};

export const rawDelta = (measures: MD.Measures, comparedMeasure: MD.Measure = null): number => {
    return (comparedMeasure ? comparedMeasure.value : measures[measures.length - 1].value) - measures[0].value;
};

export const severityResolver = (measure: MD.Measure): SR.SeverityDetails => {
    return MP.MeasurePropertiesMap[measure.type].severityResolver(measure.value);
};

export const score = (measure: MD.Measure): number => {
    let measureProperties: MP.MeasureProperties = MP.MeasurePropertiesMap[measure.type];
    // let clippedMeasureValue = Math.min(Math.max(measure.value, 0), measureProperties.maxScore);
    // const score = measureProperties.lowerIsBetter ? measureProperties.maxScore - clippedMeasureValue : clippedMeasureValue;
    const score = measureProperties.lowerIsBetter ? measureProperties.maxScore - measure.value : measure.value;
    return (score / measureProperties.maxScore) * 100;
};

export const scoreDescription = (measures: MD.Measure | MD.Measures): string => {
    return MP.MeasurePropertiesMap[type(measures)].scoreDescription
};

export const scoreRangeDisplay = (measures: MD.Measure | MD.Measures): string => {
    return MP.MeasurePropertiesMap[type(measures)].scoreRangeDisplay
};

export const tooltip = (mt: MD.MeasureType): string => {
    return MP.MeasurePropertiesMap[mt].tooltip;
};

export const bestRecentRomMeasure = (measures: MD.Measures, range: number): MD.Measure => {
    if (!measures || measures.length === 0) {
        return null;
    }

    const start = Math.max(0, measures.length - range);

    // Work backwards through the measures from latest to earliest week
    return measures
        .slice(start)
        .reduceRight(
            (best, measure) => {
                if (!best || measure.value > best.value) {
                    return measure;
                }
                return best;
            },
            null
        );
};

export const romDelta = (measureType: MD.MeasureType, measurementA: number, measurementB: number): number => {
    if (measurementA === 0 && measurementB === 0) {
        return 0;
    }

    let range: number
    if (isUpperRomType(measureType)) {
        range = 180;
    }
    else if (isLowerRomType(measureType)) {
        range = 90;
    }
    else if (isNeckRomType(measureType)) {
        switch (measureType) {
            case MD.MeasureTypesMap.NeckExtension:
                range = 60;
                break;
            case MD.MeasureTypesMap.NeckFlexion:
                range = 50;
                break;
            case MD.MeasureTypesMap.NeckLeftRotation:
            case MD.MeasureTypesMap.NeckRightRotation:
                range = 80;
                break;
        }
    }
    else {
        throw new Error(`[romDelta] Invalid measure type: ${measureType}`);
    }
    const nudgedMeasureA = measurementA === 0 ? 1 : measurementA;
    return (measurementB - nudgedMeasureA) / range;
};